import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
// TASK-67 PR-D: pickFirstTwoPrimaries derives the canonical "first 2
// primaries by joined_at" pair from team_members — replaces direct
// teams.player1/2 reads + the two profile-via-FK embeds.
import { pickFirstTwoPrimaries } from "@/lib/teamHelpers";
// MEGA-SPRINT Sprint 4 PR 4.1 — dual-identity team_member display.
import { resolveTeamMember } from "@/lib/playerDisplay";

interface StandingsTableProps {
  divisionId: string;
  teamId: string;
  promotionCount: number;
  relegationCount: number;
}

export default function StandingsTable({ divisionId, teamId, promotionCount, relegationCount }: StandingsTableProps) {
  const { data: standings, isLoading } = useQuery({
    queryKey: ["division-standings", divisionId],
    queryFn: async () => {
      // TASK-67 PR-D: replaced player1/player2 PostgREST embeds with a
      // single team_members embed — renderer derives the first 2 primaries
      // (by joined_at) via pickFirstTwoPrimaries.
      const { data, error } = await supabase
        .from("standings")
        .select(`
          *,
          team:teams!standings_team_id_fkey(
            id, team_name,
            members:team_members(user_id, customer_id, role, joined_at,
              profile:profiles!team_members_user_id_fkey(full_name),
              customer:customers!team_members_customer_id_fkey(full_name, email))
          )
        `)
        .eq("division_id", divisionId)
        .order("position", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!divisionId,
  });

  if (isLoading) {
    return <Skeleton className="h-60 w-full rounded-lg" />;
  }

  if (!standings || standings.length === 0) {
    return (
      <div className="py-10 text-center text-muted-foreground">
        <p>Tabellen är inte tillgänglig ännu</p>
      </div>
    );
  }

  const totalTeams = standings.length;

  // Sprint 4 PR 4.1: dual-identity (user OR customer) team_member shape.
  type StandingMember = {
    user_id: string | null;
    customer_id: string | null;
    role: "primary" | "reserve";
    joined_at: string;
    profile: { full_name: string | null } | null;
    customer: { full_name: string | null; email: string | null } | null;
  };

  const firstNameOf = (m: StandingMember | null): string => {
    if (!m) return "?";
    const r = resolveTeamMember(m);
    if (r.kind === "none") return "?";
    return r.displayName.split(" ")[0] || "?";
  };

  const getTeamLabel = (team: any) => {
    if (!team) return "—";
    if (team.team_name) return team.team_name;
    const members = (team.members ?? null) as StandingMember[] | null;
    const [p1, p2] = pickFirstTwoPrimaries(members);
    return `${firstNameOf(p1)} / ${firstNameOf(p2)}`;
  };

  const getRowClass = (position: number, rowTeamId: string) => {
    const classes: string[] = [];
    if (position <= promotionCount) {
      classes.push("bg-primary/10");
    } else if (position > totalTeams - relegationCount) {
      classes.push("bg-destructive/10");
    }
    if (rowTeamId === teamId) {
      classes.push("font-bold");
    }
    return cn(classes);
  };

  return (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10 text-center">#</TableHead>
            <TableHead>Lag</TableHead>
            <TableHead className="w-10 text-center">S</TableHead>
            <TableHead className="w-10 text-center">V</TableHead>
            <TableHead className="w-10 text-center">O</TableHead>
            <TableHead className="w-10 text-center">F</TableHead>
            <TableHead className="w-16 text-center">Set</TableHead>
            <TableHead className="w-16 text-center">Game</TableHead>
            <TableHead className="w-12 text-center">P</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {standings.map((row) => (
            <TableRow key={row.id} className={getRowClass(row.position, (row as any).team?.id)}>
              <TableCell className="text-center">{row.position}</TableCell>
              <TableCell className="truncate max-w-[120px]">
                {getTeamLabel((row as any).team)}
              </TableCell>
              <TableCell className="text-center">{row.played}</TableCell>
              <TableCell className="text-center">{row.wins}</TableCell>
              <TableCell className="text-center">{row.draws}</TableCell>
              <TableCell className="text-center">{row.losses}</TableCell>
              <TableCell className="text-center text-xs">
                {row.sets_won}-{row.sets_lost}
              </TableCell>
              <TableCell className="text-center text-xs">
                {row.games_won}-{row.games_lost}
              </TableCell>
              <TableCell className="text-center font-semibold">{Number(row.points)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex gap-4 p-2 text-xs text-muted-foreground border-t">
        {promotionCount > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm bg-primary/20" /> Uppflyttning
          </span>
        )}
        {relegationCount > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm bg-destructive/20" /> Nedflyttning
          </span>
        )}
      </div>
    </div>
  );
}
